import { Component, Input, OnInit, TemplateRef, ViewChild } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import moment from 'moment';
import { AuthService } from 'src/app/services/auth.service';
import { ClientsService } from 'src/app/services/clients.service';
import { ClientPlan } from 'src/app/shared/models/client_plan.model';

@Component({
    selector: 'app-client-plans',
    templateUrl: './client-plans.component.html',
    styleUrl: './client-plans.component.scss',
    standalone: false
})
export class ClientPlansComponent implements OnInit {
	@ViewChild('createPlanModal') createPlanModal!: TemplateRef<any>;
	@ViewChild('successModal') successModal!: TemplateRef<any>;
	@ViewChild('cancelModal') cancelModal!: TemplateRef<any>;

	loading = true;
	plans: any[] = [];
	@Input() currentClient!: any;

	createPlanModalRef: any;
	successModalRef: any;
	
	createForm!: UntypedFormGroup;
	submitted = false;
	spinner = false;
	success = false;
	message = '';

	minStartDate!: Date;
	minExpiryDate: Date = new Date();

	head_pose_analysis = false
	skin_analysis = false
	bl_analysis = false
	voice_analysis = false
	emotion_analysis = false
	api_access = false

	editMode = false;
	currentPlan!: ClientPlan;

	plan_cycles: any[] = [
		{ name: 'Monthly', value: 'MONTHLY' },
		{ name: 'Yearly', value: 'YEARLY' }
	];

	constructor(private clientsService: ClientsService, private authService: AuthService, private modalService: NgbModal, private formBuilder: UntypedFormBuilder) {}

	get f() { return this.createForm.controls; }
	
	ngOnInit(): void {
		this.getClientPlans();
	}
	
	getClientPlans() {
		this.clientsService.getClientPlans(this.currentClient.id).subscribe({
			next: (response: any) => {
				this.plans = response;
				this.loading = false;
			},
			error: (err) => {
				this.authService.toastErrorMessage(err.message ? err.message : err, 'Client Plans');
			}
		});
	}

	onCheckedDone(name: string) {
		switch (name) {
			case 'skin':
				this.skin_analysis = !this.skin_analysis;
				break;
			case 'voice':
				this.voice_analysis = !this.voice_analysis;
				break;
			case 'bl':
				this.bl_analysis = !this.bl_analysis;
				break;
			case 'emotion':
				this.emotion_analysis = !this.emotion_analysis;
				break;
			case 'api':
				this.api_access = !this.api_access;
				break;
			case 'headpose':
				this.head_pose_analysis = !this.head_pose_analysis;
				break;
		}
	}

	getStatus(plan: ClientPlan) {
		if (!plan.expired_at) {
			return true;
		}

		const today = new Date();
		const expired = new Date(plan.expired_at);

		return expired > today;
	}

	openCancelModal(plan: ClientPlan) {
		this.currentPlan = plan;

		if (this.getStatus(plan)) {
			this.modalService.open(this.cancelModal, { size: 'l', backdrop: 'static', keyboard : false });
		}
	}

	cancelPlan() {
		this.clientsService.cancelClientPlan(this.currentClient.id, this.currentPlan.id).subscribe({
			next: () => {
				this.submitted = false;
				this.spinner = false;
				this.createPlanModalRef.close();
				this.success = true;
				this.modalService.open(this.successModal, { size: 'l', backdrop: 'static', keyboard : false });
			},
			error: (err) => {
				this.success = false;
				this.submitted = false;
				this.spinner = false;
				this.authService.toastErrorMessage(err.message ? err.message : err , 'Plan Update!');
			}
		});
	}

	openModal(plan?: ClientPlan) {
		this.submitted = false;
		this.spinner = false;
		this.success = false;
		this.editMode = false;

		this.minStartDate = new Date();
		this.minStartDate.setDate(this.minStartDate.getDate());
		this.minExpiryDate.setDate(this.minStartDate.getDate() + 1);

		if (plan) {
			this.editMode = true;
			this.currentPlan = plan;
			this.createForm = this.formBuilder.group({
				name: [{ value: plan.name, disabled: true }],
				plan_cycle: [{ value: plan.plan_cycle, disabled: true }],
				started_at: [{ value: moment(plan.started_at).format('YYYY-MM-DD'), disabled: true }],
				expired_at: [{ value: plan.expired_at ? moment(plan.expired_at).format('YYYY-MM-DD') : null, disabled: true }],
				price: [plan.configuration?.price, [Validators.min(1)]],
				examinees: [plan.configuration?.examinees, [Validators.required]],
				video_minutes: [plan.configuration?.video_minutes, [Validators.required]]
			});

			this.skin_analysis = plan.configuration.skin_analysis;
			this.bl_analysis = plan.configuration.bl_analysis;
			this.voice_analysis = plan.configuration.voice_analysis;
			this.emotion_analysis = plan.configuration.emotion_analysis;
			this.api_access = plan.configuration.api_access;
			this.head_pose_analysis = plan.configuration['head-pose_analysis'];

		} else {
			this.createForm = this.formBuilder.group({
				name: ['', [Validators.required]],
				plan_cycle: [this.plan_cycles[0].value, [Validators.required]],
				started_at: [null, [Validators.required]],
				expired_at: [null],
				price: [null, [Validators.min(1)]],
				examinees: [null, [Validators.required]],
				video_minutes: [null, [Validators.required]]
			});
		}

		this.createPlanModalRef = this.modalService.open(this.createPlanModal, { size: 'l', backdrop: 'static', keyboard : false });
	}

	onSubmit() {
		this.submitted = true;
		this.spinner = true;

		if (this.createForm.invalid) {
			this.spinner = false;
			return;
		}

		if (!this.skin_analysis && !this.bl_analysis && !this.voice_analysis && !this.emotion_analysis) {
			this.spinner = false;
			return;
		}

		const data = {
			name: this.editMode ? this.currentPlan.name : this.createForm.value.name,
			started_at: this.editMode ? this.currentPlan.started_at : this.createForm.value.started_at,
			expired_at: this.editMode ? this.currentPlan.expired_at : this.createForm.value.expired_at,
			configuration: {
				price: this.createForm.value.price,
				examinees: this.createForm.value.examinees,
				video_minutes: this.createForm.value.video_minutes,
				skin_analysis: this.skin_analysis,
				bl_analysis: this.bl_analysis,
				voice_analysis: this.voice_analysis,
				emotion_analysis: this.emotion_analysis,
				api_access: this.api_access,
				'head-pose_analysis': this.head_pose_analysis
			}
		}

		if (this.editMode) {
			this.clientsService.updateClientPlan(this.currentClient.id, this.currentPlan.id, data).subscribe({
				next: () => {
					this.submitted = false;
					this.spinner = false;
					this.createPlanModalRef.close();
					this.success = true;
					this.modalService.open(this.successModal, { size: 'l', backdrop: 'static', keyboard : false });
				},
				error: (err) => {
					this.success = false;
					this.submitted = false;
					this.spinner = false;
					this.authService.toastErrorMessage(err.message ? err.message : err , 'Plan Update!');
				}
			});
		} else {
			this.clientsService.createClientPlan(this.currentClient.id, data).subscribe({
				next: () => {
					this.submitted = false;
					this.spinner = false;
					this.createPlanModalRef.close();
					this.success = true;
					this.modalService.open(this.successModal, { size: 'l', backdrop: 'static', keyboard : false });
				},
				error: (err) => {
					this.success = false;
					this.submitted = false;
					this.spinner = false;
					this.authService.toastErrorMessage(err.message ? err.message : err , 'Plan Creation!');
				}
			});
		}
	}

	closeModal() {
		if (this.success) {
			this.getClientPlans();
		}
		
		this.createForm.reset();
		this.modalService.dismissAll();
	}
}